import React from 'react';
import { Field, useDidUpdateEffect } from '@odin-labs/components';
import { useDebouncedValue, useUpdatableState } from 'utils';

export type FormSubmissionWorkerEditSearchBoxProps = {
  onSearchTermChange: (searchTerm: string) => void;
  /** Called on Enter once the typed text has reached `onSearchTermChange`. */
  onEnterKey?: () => void;
};

export function FormSubmissionWorkerEditSearchBox({
  onSearchTermChange,
  onEnterKey,
}: FormSubmissionWorkerEditSearchBoxProps): React.ReactElement {
  // useUpdatableState hook is used here for updating the state based on props
  const [searchValue, setSearchValue] = useUpdatableState<string>('');

  const inputRef = React.useRef<HTMLInputElement>();
  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const debouncedSearchValue = useDebouncedValue(searchValue, 300);
  useDidUpdateEffect(() => {
    onSearchTermChange(debouncedSearchValue);
  }, [debouncedSearchValue]);

  return (
    <Field
      ref={inputRef}
      name="workerSearchBox"
      value={searchValue}
      onChange={(value: string): void => setSearchValue(value)}
      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>): void => {
        // an Enter that confirms IME composition belongs to the typed text
        if (onEnterKey && e.key === 'Enter' && !e.nativeEvent.isComposing) {
          e.preventDefault();
          // until the debounce catches up, the results belong to the previous text
          if (searchValue === debouncedSearchValue) {
            onEnterKey();
          }
        }
      }}
      placeholder="Search workers"
    />
  );
}
